import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  previewEmailPersonalization,
  type PersonalizationPreviewResult,
} from '../../lib/previewEmailPersonalization';
import { parseScheduledEmailPersonalization } from '../../lib/personalization';
import { buildPersonalizationPromptView } from '../../lib/personalizationPrompt';
import {
  diffAfterSegments,
  diffBeforeSegments,
  htmlToPlainText,
  type DiffSegment,
} from '../../lib/textDiff';
import type { ScheduledEmailRow } from './types';

interface PersonalizationPreviewPanelProps {
  onClose: () => void;
  subscriberId: string;
  subscriberEmail?: string | null;
  subject: string;
  bodyHtml: string;
  minScore: number;
  existing?: ScheduledEmailRow | null;
}

function DiffText({ segments, side }: { segments: DiffSegment[]; side: 'before' | 'after' }) {
  return (
    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
      {segments.map((seg, i) => {
        if (seg.type === 'same') {
          return <span key={i}>{seg.text}</span>;
        }
        if (side === 'before' && seg.type === 'remove') {
          return (
            <mark
              key={i}
              className="bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100 rounded px-0.5"
            >
              {seg.text}
            </mark>
          );
        }
        if (side === 'after' && seg.type === 'add') {
          return (
            <mark
              key={i}
              className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-100 rounded px-0.5"
            >
              {seg.text}
            </mark>
          );
        }
        return null;
      })}
    </p>
  );
}

function PromptBlock({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{label}</div>
      <pre className="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-x-auto text-[10px] whitespace-pre-wrap break-words max-h-56 overflow-y-auto">
        {text}
      </pre>
    </div>
  );
}

function outcomeLabel(outcome: PersonalizationPreviewResult['outcome']): string {
  switch (outcome) {
    case 'completed':
      return 'Personalized';
    case 'skipped':
      return 'Skipped (low profile)';
    case 'failed':
      return 'Failed';
  }
}

export default function PersonalizationPreviewPanel({
  onClose,
  subscriberId,
  subscriberEmail,
  subject,
  bodyHtml,
  minScore,
  existing,
}: PersonalizationPreviewPanelProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PersonalizationPreviewResult | null>(null);
  const [bodyView, setBodyView] = useState<'diff' | 'html'>('diff');

  const storedPersonalization = useMemo(
    () => parseScheduledEmailPersonalization(existing?.personalization),
    [existing?.personalization],
  );

  const storedResult = useMemo((): PersonalizationPreviewResult | null => {
    if (!storedPersonalization?.result) return null;
    const base = storedPersonalization.base;
    return {
      outcome:
        storedPersonalization.status === 'completed'
          ? 'completed'
          : storedPersonalization.status === 'skipped'
            ? 'skipped'
            : 'failed',
      base: {
        subject: base.subject?.trim() || existing?.subject?.trim() || '',
        body_html: base.body_html?.trim() || existing?.body_html?.trim() || '',
      },
      result: storedPersonalization.result,
      profile_score: storedPersonalization.score_at_run ?? null,
      profile_score_tier: null,
      min_score: storedPersonalization.min_score,
      skip_reason: storedPersonalization.skip_reason,
      error: storedPersonalization.error,
      context_snapshot: storedPersonalization.context_snapshot,
      context_payload: storedPersonalization.context_payload,
      changes: storedPersonalization.changes,
      subscriber_email: subscriberEmail ?? null,
    };
  }, [storedPersonalization, existing, subscriberEmail]);

  const contextPayload =
    result?.context_payload ??
    storedPersonalization?.context_payload ??
    null;
  const changeNotes = result?.changes ?? storedPersonalization?.changes ?? null;

  const runPreview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await previewEmailPersonalization({
        scheduled_email_id: existing?.id,
        subscriber_id: subscriberId,
        subject: subject.trim(),
        body_html: bodyHtml.trim(),
        min_score: minScore,
      });
      setResult(data);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [existing?.id, subscriberId, subject, bodyHtml, minScore]);

  useEffect(() => {
    setResult(null);
  }, [existing?.id, subscriberId, subject, bodyHtml, minScore]);

  const active = result ?? storedResult;

  const resultSubject = active?.result?.subject ?? active?.base.subject ?? '';
  const resultBody = active?.result?.body_html ?? active?.base.body_html ?? '';
  const baseSubject = active?.base.subject ?? subject;
  const baseBody = active?.base.body_html ?? bodyHtml;

  const subjectBefore = diffBeforeSegments(baseSubject, resultSubject);
  const subjectAfter = diffAfterSegments(baseSubject, resultSubject);
  const plainBefore = htmlToPlainText(baseBody);
  const plainAfter = htmlToPlainText(resultBody);
  const bodyBefore = diffBeforeSegments(plainBefore, plainAfter);
  const bodyAfter = diffAfterSegments(plainBefore, plainAfter);

  const subjectChanged = baseSubject !== resultSubject;
  const bodyChanged = plainBefore !== plainAfter;

  const promptView = useMemo(() => {
    if (result?.prompt) return result.prompt;
    if (!contextPayload) return null;
    return buildPersonalizationPromptView(
      contextPayload,
      baseSubject,
      baseBody,
    );
  }, [result?.prompt, contextPayload, baseSubject, baseBody]);

  return (
    <aside className="w-full sm:w-[22rem] lg:w-[26rem] shrink-0 border-l border-gray-200 dark:border-gray-700 flex flex-col bg-gray-50/80 dark:bg-gray-900/40 min-h-0">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-violet-500" />
          AI preview
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          aria-label="Close AI preview panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {subscriberEmail ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={subscriberEmail}>
            Subscriber: <span className="font-medium text-gray-700 dark:text-gray-200">{subscriberEmail}</span>
          </p>
        ) : null}

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Runs the same OpenAI step as production (no send, no DB update). Uses current subject/body
          from the form.
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={loading || !subject.trim() || !bodyHtml.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 rounded-lg"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {loading ? 'Running…' : 'Run preview'}
          </button>
          {storedResult && !result ? (
            <button
              type="button"
              onClick={() => setResult(storedResult)}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-white dark:hover:bg-gray-800"
            >
              Show saved result
            </button>
          ) : null}
        </div>

        {!active && !loading ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
            Run preview to compare original vs AI-tuned copy. Red = removed, green = added.
          </p>
        ) : null}

        {active ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span
                className={`px-2 py-0.5 rounded-full font-medium ${
                  active.outcome === 'completed'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                    : active.outcome === 'skipped'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                }`}
              >
                {outcomeLabel(active.outcome)}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                Score {active.profile_score ?? '—'} / min {active.min_score}
              </span>
            </div>

            {active.error ? (
              <p className="text-xs text-red-700 dark:text-red-300 break-words">{active.error}</p>
            ) : null}
            {active.skip_reason ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Skip reason: {active.skip_reason}
              </p>
            ) : null}

            {!subjectChanged && !bodyChanged && active.outcome === 'completed' ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">No copy changes detected.</p>
            ) : null}

            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-300">Subject</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Original</div>
                  <DiffText segments={subjectBefore} side="before" />
                </div>
                <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Preview</div>
                  <DiffText segments={subjectAfter} side="after" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">Body</div>
                <div className="flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden text-[10px]">
                  <button
                    type="button"
                    onClick={() => setBodyView('diff')}
                    className={`px-2 py-0.5 ${bodyView === 'diff' ? 'bg-gray-200 dark:bg-gray-600' : ''}`}
                  >
                    Diff
                  </button>
                  <button
                    type="button"
                    onClick={() => setBodyView('html')}
                    className={`px-2 py-0.5 ${bodyView === 'html' ? 'bg-gray-200 dark:bg-gray-600' : ''}`}
                  >
                    HTML
                  </button>
                </div>
              </div>

              {bodyView === 'diff' ? (
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Original (text)</div>
                    <DiffText segments={bodyBefore} side="before" />
                  </div>
                  <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Preview (text)</div>
                    <DiffText segments={bodyAfter} side="after" />
                  </div>
                </div>
              ) : (
                <div
                  className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm prose prose-sm dark:prose-invert max-w-none max-h-48 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: resultBody }}
                />
              )}
            </div>

            {changeNotes && changeNotes.length > 0 ? (
              <details className="text-xs text-gray-600 dark:text-gray-300" open>
                <summary className="cursor-pointer font-medium">What changed</summary>
                <ul className="mt-1 space-y-1.5 list-disc pl-4">
                  {changeNotes.map((c, i) => (
                    <li key={i}>
                      <span className="font-medium">{c.area}:</span> {c.rationale}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {contextPayload ? (
              <details className="text-xs text-gray-500 dark:text-gray-400" open>
                <summary className="cursor-pointer font-medium text-gray-600 dark:text-gray-300">
                  Reader profile (JSON)
                </summary>
                <pre className="mt-1 p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-x-auto text-[10px] max-h-48 overflow-y-auto">
                  {JSON.stringify(contextPayload, null, 2)}
                </pre>
              </details>
            ) : null}

            {promptView ? (
              <details className="text-xs text-gray-500 dark:text-gray-400" open>
                <summary className="cursor-pointer font-medium text-gray-600 dark:text-gray-300">
                  Full prompt sent to OpenAI
                </summary>
                <div className="mt-2 space-y-2">
                  <p className="text-[10px] text-gray-400">
                    Model: <span className="font-mono text-gray-600 dark:text-gray-300">{promptView.model}</span>
                    {' · '}
                    temperature 0.35 · JSON response
                  </p>
                  <PromptBlock label="System message" text={promptView.system} />
                  <PromptBlock label="User message" text={promptView.user} />
                </div>
              </details>
            ) : null}

            {active.context_snapshot ? (
              <details className="text-xs text-gray-500 dark:text-gray-400">
                <summary className="cursor-pointer text-gray-500">Summary stats</summary>
                <pre className="mt-1 p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-x-auto text-[10px]">
                  {JSON.stringify(active.context_snapshot, null, 2)}
                </pre>
              </details>
            ) : null}
          </>
        ) : null}
      </div>
    </aside>
  );
}
